import styled from 'styled-components'

export const FormCard = styled.div`
  position: relative;
  background: ${({ theme }) => theme.whiteColor};
  border: 0.0625rem solid ${({ theme }) => theme.grey300Color};
  border-radius: 0.75rem;
  padding: 1.5rem 1.75rem 1.75rem;
  margin-bottom: 1rem;
`

export const FormHeading = styled.h2`
  margin: 0;
  font-family: Rubik, verdana, sans-serif;
  font-size: 1.0625rem;
  font-weight: 600;
  color: ${({ theme }) => theme.grey900Color};
`

export const FormSubtitle = styled.p`
  margin: 0.25rem 0 0;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.grey700Color};
`

// BRAND-9511: "N of M runs used" line under the heading — only rendered
// when the BE sends max_sourcing_runs on the sourcing block.
export const RunsUsedNote = styled.p`
  margin: 0.375rem 0 0;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.grey700Color};
`

export const CloseButton = styled.button`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  border: none;
  background: none;
  padding: 0.25rem;
  line-height: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.grey700Color};
`

// BRAND-9663: opt-in "Generate Criteria" rerun toggle row (BRAND-9605 tier
// 2) — same card treatment as ChannelRow so it reads as one more control in
// the same visual family, not a bolted-on banner.
export const GenerateCriteriaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 3.25rem;
  padding: 0 1rem;
  margin: 1.375rem 0 0.25rem;
  border-radius: 0.5rem;
  border: 0.0625rem solid ${({ theme }) => theme.grey300Color};
  background: ${({ theme }) => theme.whiteColor};
`

export const GenerateCriteriaLabel = styled.span`
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.grey900Color};
`

export const SectionLabel = styled.div`
  margin: 1.375rem 0 0.5rem;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.6875rem;
  font-weight: 500;
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  color: ${({ theme }) => theme.grey700Color};
`

// Plain div, not a button: the file-pick target is AttachRowButton below,
// so Remove/Replace render as its siblings instead of nesting a button
// inside a button (invalid HTML). $disabled mirrors that button's own
// disabled state so the whole row dims together.
export const AttachRow = styled.div<{ $attached: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;

  /* Was a <button>, which is border-box by default; a div is not, so width:100%
     plus the padding/border below overflowed the card by 2rem. */
  box-sizing: border-box;
  width: 100%;
  min-height: 3rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 0.0625rem ${({ $attached }) => ($attached ? 'solid' : 'dashed')}
    ${({ theme, $attached }) => ($attached ? theme.grey300Color : theme.grey400Color)};
  background: ${({ theme, $attached }) => ($attached ? theme.grey100Color : theme.whiteColor)};
  opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};
`

// Shrinks (min-width: 0) so AttachRowLabel's filename is what truncates —
// AttachRowActions (Replace/Remove) never shrinks below its content.
export const AttachRowMain = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1 1 auto;
  min-width: 0;
`

// The real file-pick button — covers just the icon + filename/CTA label
// (AttachRow itself is a plain div, see above). Accessible name comes from
// its own text content, same as before this was split out.
export const AttachRowButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.grey900Color};
  cursor: pointer;
  text-align: left;

  &:disabled {
    cursor: not-allowed;
  }
`

// The filename/CTA text specifically (previously this ellipsis/nowrap lived
// on AttachRowMain itself, which clipped the icon and Replace badge along
// with it on narrow screens — see AttachRow's doc above).
export const AttachRowLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

// flex-shrink: 0 (via `flex: none`) so the caption never squeezes Replace —
// it wraps onto its own line (AttachRow's flex-wrap) instead of shrinking.
export const AttachRowHint = styled.span`
  flex: none;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.grey700Color};
`

// BRAND-9515: shown while the attach_transcript/attach_notes rel is still
// present after a file has been attached; disappears once a run begins.
//
// BRAND-9582 item 13: needs its own onClick now — it moved out of
// AttachRow (no longer a button) into AttachRowActions, so nothing bubbles
// to it anymore. Stays a plain <span>, not a <button> (no independent
// keyboard target existed for it before either).
export const ReplaceAffordance = styled.span`
  flex: none;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.primary200Color};
  color: ${({ theme }) => theme.primaryColor};
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
`

// Right-aligned pair — Replace + Remove render together as one action
// group instead of Remove standing alone below the row.
export const AttachRowActions = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  flex: none;
`

// Real, separately-clickable button — sibling of AttachRowButton in
// AttachRowActions (see AttachRow's note above), never nested inside it.
// Clicking dispatches the real detachTranscript/detachNotes mutation.
// Colours are theme error tokens only (errorColor/error100Color), matching
// ReplaceAffordance's pill shape/scale so the pair reads as primary/danger.
export const RemoveAffordance = styled.button`
  display: inline-flex;
  align-items: center;
  flex: none;
  padding: 0.125rem 0.5rem;
  border: 0.0625rem solid ${({ theme }) => theme.errorColor};
  border-radius: 999px;
  background: ${({ theme }) => theme.error100Color};
  color: ${({ theme }) => theme.errorColor};
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;

  /* STYLELINT TRAP (no-descending-specificity): &:disabled MUST stay
     BEFORE &:hover:not(:disabled) in this block — the reverse order broke
     a build on this branch before. */
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:hover:not(:disabled) {
    opacity: 0.85;
  }
`

export const NotesTextArea = styled.textarea`
  display: block;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-height: 5.5rem;
  margin-top: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  border: 0.0625rem solid ${({ theme }) => theme.grey300Color};
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.grey900Color};
  resize: vertical;

  &::placeholder {
    color: ${({ theme }) => theme.grey700Color};
  }
`

export const FieldCaption = styled.div`
  margin-top: 0.375rem;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.grey700Color};
`

// BRAND-9688: no longer the flex row itself — split into ChannelRowMain
// below so a channel (YouTube) can stack an optional second row (the
// similar-handle input) underneath its toggle/stepper line without fighting
// this container's own flex layout. TikTok/Instagram render identically to
// before, just with one extra wrapping div around their existing content.
export const ChannelRow = styled.div<{ $disabled?: boolean }>`
  padding: 0 1rem;
  margin-bottom: 0.5rem;
  border-radius: 0.5rem;
  border: 0.0625rem solid ${({ theme }) => theme.grey300Color};
  background: ${({ theme }) => theme.whiteColor};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`

export const ChannelRowMain = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 3.25rem;
`

export const ChannelName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.grey900Color};
`

export const ChannelDisabledHint = styled.span`
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.grey600Color};
`

export const ChannelSpacer = styled.span`
  flex: 1;
`

// Minimal accessible switch (no toggle exists in @tribegroup).
export const SwitchButton = styled.button<{ $on: boolean }>`
  flex: none;
  position: relative;
  width: 2.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  border-radius: 0.75rem;
  background: ${({ theme, $on }) => ($on ? '#22c55e' : theme.grey400Color)};
  cursor: pointer;
  transition: background 120ms ease;

  &::after {
    content: '';
    position: absolute;
    top: 0.125rem;
    left: ${({ $on }) => ($on ? '1.125rem' : '0.125rem')};
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    background: ${({ theme }) => theme.whiteColor};
    box-shadow: 0 0.0625rem 0.1875rem rgb(0 0 0 / 25%);
    transition: left 120ms ease;
  }

  &:disabled {
    cursor: not-allowed;
  }
`

export const StepperGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
`

export const StepperButton = styled.button`
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 0.0625rem solid ${({ theme }) => theme.grey300Color};
  background: ${({ theme }) => theme.whiteColor};
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.9375rem;
  line-height: 1;
  color: ${({ theme }) => theme.grey900Color};
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

export const StepperCount = styled.span`
  min-width: 1.25rem;
  text-align: center;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.grey900Color};
`

export const StepperUnit = styled.span`
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.grey700Color};
`

// BRAND-9688: optional "find channels similar to @handle" sub-row, rendered
// only inside the YouTube ChannelRow (see ChannelRowMain split above) and
// only behind youTubeSimilarSearchEnabled.
export const SimilarHandleRow = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 0.0625rem dashed ${({ theme }) => theme.grey300Color};
`

export const SimilarHandleLabel = styled.div`
  margin-bottom: 0.375rem;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.grey900Color};
`

export const SimilarHandleInputWrap = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 0.0625rem solid ${({ theme }) => theme.grey300Color};
  border-radius: 0.625rem;
  padding: 0.5rem 0.75rem;
  background: ${({ theme, $disabled }) => ($disabled ? theme.grey100Color : theme.whiteColor)};

  &:focus-within {
    border-color: ${({ theme }) => theme.primaryColor};
  }
`

export const SimilarHandleAt = styled.span`
  flex: none;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.grey600Color};
`

export const SimilarHandleInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.grey900Color};

  &::placeholder {
    color: ${({ theme }) => theme.grey600Color};
  }

  &:disabled {
    cursor: not-allowed;
  }
`

export const SimilarHandleOptional = styled.span`
  flex: none;
  margin-left: auto;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.grey700Color};
`

export const SimilarHandleHint = styled.p`
  margin: 0.375rem 0 0;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.grey700Color};
`

// BRAND-9702: shown instead of SimilarHandleHint (never alongside it) once
// the typed value fails the bare-handle shape check.
export const SimilarHandleError = styled.p`
  margin: 0.375rem 0 0;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.errorColor};
`

export const StartRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1.25rem;
`

export const StartButton = styled.button`
  height: 2.5rem;
  padding: 0 1.375rem;
  border: none;
  border-radius: 1.25rem;
  background: ${({ theme }) => theme.primaryColor};
  color: ${({ theme }) => theme.whiteColor};
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    background: ${({ theme }) => theme.grey300Color};
    color: ${({ theme }) => theme.grey700Color};
    cursor: not-allowed;
  }
`

export const StartLockNote = styled.span`
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.grey700Color};
`

export const HiddenFileInput = styled.input`
  display: none;
`
